import "./App.css";
import React, { useEffect, useState, Suspense, lazy } from "react";
//import "bootstrap/dist/css/bootstrap.min.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import "./utils/fcm-bridge.js"; // Import FCM bridge for Android WebView

import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";

// Static imports for core app shell/layout
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import LoadingFallback from "./components/ui/loading-fallback";
import { ToastProvider } from "./components/ui/toast";
import { TooltipProvider } from "./components/ui/tooltip";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { errorTracker } from "./utils/errorTracker";
import { activityTracker } from "./utils/activityTracker";
import UseAll from "./hooks/UseAll";
import axios from "axios";

// Lazy loaded components
const LiveStreamDashboard = lazy(
  () => import("./components/livestream/liveStreamDashBoard"),
);
const StreamRecording = lazy(
  () => import("./components/livestream/playRecording"),
);
const OnBoardingForm = lazy(() => import("./components/OnBoardingForm"));
const UseCategories = lazy(() => import("./hooks/UseCat"));
const SingleProductPage = lazy(
  () => import("./components/products/SingleProductPage"),
);
const ProductOnBoardingMain = lazy(
  () => import("./components/ProductOnBoardingMain"),
);
const Congratulations = lazy(() => import("./components/Congratulations"));
const IndustrialLeadsPage = lazy(
  () => import("./components/leads/IndustrialLeadsPage"),
);
const AccountPage = lazy(() => import("./components/AccountPage"));
const BusinessOnboarding = lazy(
  () => import("./components/BusinessOnBoarding"),
);
const CategoryManagement = lazy(
  () => import("./components/category-management"),
);
const IdeaCategoryManagement = lazy(
  () => import("./components/admin/IdeaCategoryManagement"),
);
const ExpoLeadsCampaign = lazy(
  () => import("./components/admin/ExpoLeadsCampaign"),
);
const AccountStatus = lazy(() => import("./components/AccountStatus"));
const AdminHero = lazy(() => import("./components/AdminHero"));
const BannerEdit = lazy(() => import("./components/BannerEdit"));
const ReferralsAndSubscriptions = lazy(
  () => import("./components/ReferralsAndSubscriptions"),
);
const BlogsPost = lazy(() => import("./components/blogs/BlogsPost"));
const SearchByImage = lazy(() => import("./components/SearchByImage"));
const TipsMainPage = lazy(() => import("./components/tips/TipsMainPage"));
const TendersMainPage = lazy(
  () => import("./components/tenders/TendersMainPage"),
);
const TenderFAQsPage = lazy(() => import("./components/tenders/Faq"));
const BlogMainPage = lazy(() => import("./components/blogs/BlogMainPage"));
const BlogPostPage = lazy(() => import("./components/blogs/BlogPostPage"));
const UsersDashboard = lazy(() => import("./components/UsersDashboard"));
const UserVerificationStatus = lazy(
  () => import("./components/UserVerificationStatus"),
);
const FaqPage = lazy(() => import("./components/FaqPage"));
const LoginForgotPasswordPage = lazy(
  () => import("./components/LoginForgotPasswordPage"),
);
const SellerOnboarding = lazy(() => import("./components/sellerOnboarding"));
const PreOnboardingPage = lazy(() => import("./components/preOnboardingPage"));
const LogisticsOnboardingForm = lazy(
  () => import("./components/logesticsOnboardingtemp"),
);
const DynamicPostingForm = lazy(
  () => import("./components/franchBusinessPosting"),
);
const ServiceOnboardingtemp = lazy(
  () => import("./components/ServiceOnboardingtemp"),
);
const SellerDashboard = lazy(() => import("./components/seller-dashboard"));
const UserDashboard = lazy(() => import("./components/user-dashboard"));
const FranchiseListingPage = lazy(
  () => import("./components/franchise/FranchiseListingPage"),
);
const FranchiseDetail = lazy(
  () => import("./components/franchise/FranchiseDetail"),
);
const BusinessListingPage = lazy(
  () => import("./components/business/BusinessListingPage"),
);
const BusinessDetailPage = lazy(
  () => import("./components/business/BusinessDetailPage"),
);
const LogisticsListingPage = lazy(
  () => import("./components/Logistics/LogisticsListingPage"),
);
const LogisticsDetailPage = lazy(
  () => import("./components/Logistics/LogisticsDetailPage"),
);
const ServicesListingPage = lazy(
  () => import("./components/services/ServicesListingPage"),
);
const ServiceDetailPage = lazy(
  () => import("./components/services/ServiceDetailPage"),
);
const EscrowPreLanding = lazy(
  () => import("./components/applications/prelandings/EscrowPreLanding"),
);
const MarineInsurancePreLanding = lazy(
  () =>
    import("./components/applications/prelandings/MarineInsurancePreLanding"),
);
const MSMEInspectionPreLanding = lazy(
  () =>
    import("./components/applications/prelandings/MSMEInspectionPreLanding"),
);
const MSMELoanPreLanding = lazy(
  () => import("./components/applications/prelandings/MSMELoanPreLanding"),
);
const SubcategoryProductPage = lazy(
  () => import("./components/products/SubcategoryProductPage"),
);
const ProductTypePage = lazy(
  () => import("./components/products/ProductTypePage"),
);
const SellerMainPage = lazy(
  () => import("./components/sellers/SellerMainPage"),
);
const SellerProfilePage = lazy(
  () => import("./components/sellers/SellerProfilePage"),
);

// Wrapper component to ensure SellerProfilePage remounts when sellerId changes
const SellerProfilePageWrapper = () => {
  const { sellerId } = useParams();
  return <SellerProfilePage key={sellerId} />;
};
const B2BHomepage = lazy(() => import("./components/mainpages/B2BHomepage"));
const RFQMainPage = lazy(() => import("./components/rfq/RFQMainPage"));
const RFQPostingForm = lazy(() => import("./components/rfq/RFQPostingForm"));
const RFQSuccessPage = lazy(() => import("./components/rfq/RFQSuccessPage"));
const RFQDetailsPage = lazy(() => import("./components/rfq/RFQDetailsPage"));
const IdeaHubPage = lazy(() => import("./components/idea/IdeaHubPage"));
const IdeaDetailsPage = lazy(() => import("./components/idea/IdeaDetailsPage"));
const LiveStreamsMainPage = lazy(
  () => import("./components/livestream/LiveStreamsMainPage"),
);
const StartLivePage = lazy(
  () => import("./components/livestream/StartLivePage"),
);
const LiveStreamPlayer = lazy(
  () => import("./components/livestream/LiveStreamPlayer"),
);
const TradeShowListingPage = lazy(
  () => import("./components/tradeshow/TradeShowListingPage"),
);
const PlatformServicesPage = lazy(
  () => import("./components/platformservices/PlatformServicesPage"),
);
const ContactUsPage = lazy(
  () => import("./components/platformservices/ContactUsPage"),
);
const Careers = lazy(() => import("./components/footer-pages/careers"));
const GetToKnowPage = lazy(() => import("./components/footer-pages/getToKnow"));
const MerzadoServicesPage = lazy(
  () => import("./components/footer-pages/merzServices"),
);
const SupportPage = lazy(() => import("./components/footer-pages/support"));
const PaymentsPage = lazy(() => import("./components/footer-pages/payment"));
const PaymentCompletionPage = lazy(
  () => import("./components/payments/productPayment"),
);
const OrderSummary = lazy(() => import("./components/orders/OrderSummary"));
const LocalSupplierPage = lazy(
  () => import("./components/sellers/LocalSupplierPage"),
);
const ProductSearchResults = lazy(
  () => import("./components/searchresults/ProductSearchResults"),
);
const CategorySearchResults = lazy(
  () => import("./components/searchresults/CategorySearchResults"),
);
const WishlistPage = lazy(() => import("./components/wishlist/WishlistPage"));
const FollowingPage = lazy(
  () => import("./components/following/FollowingPage"),
);
const FranchiseDashboard = lazy(
  () => import("./components/dashboards/franchise-dashboard"),
);
const BusinessServicesDashboard = lazy(
  () => import("./components/dashboards/business-services-dashboard"),
);
const LogisticsDashboardMain = lazy(
  () => import("./components/logistics-dashboard-main"),
);
const RFQResponsesPage = lazy(() => import("./components/rfq/rfqresponses"));
const RFQResponsePage = lazy(() => import("./components/rfq/rfqrespond"));
const SubscriptionPage = lazy(
  () => import("./components/subscriptions/subscription-page"),
);
const ProtectedSubscriptionRoute = lazy(
  () => import("./components/ProtectedSubscriptionRoute"),
);
const ProtectedVerifiedRoute = lazy(
  () => import("./components/ProtectedVerifiedRoute"),
);
const EnhancedChatComponent = lazy(() => import("./components/chat-component"));
const WatchLiveStreamPage = lazy(
  () => import("./components/livestream/WatchLiveStreamPage"),
);
const JoinMerzadoPage = lazy(
  () => import("./components/joinbusiness/join-merzado-page"),
);
const LeadScrapingUI = lazy(
  () => import("./components/lead-ai/lead-scraping-ui"),
);
const UserProfile = lazy(() => import("./components/UserProfile"));
const ServiceDashboard = lazy(
  () => import("./components/dashboards/ServiceDashboard"),
);
const CookiePopup = lazy(() => import("./components/CookiePopup"));
const SignupPage = lazy(() => import("./components/auth/signup-page"));
const LoginPage = lazy(() => import("./components/auth/login-page"));
const MainProductPage = lazy(
  () => import("./components/products/MainProductPage"),
);
const CategoryDropdown = lazy(() => import("./components/categorysubcat"));
const AdminRegister = lazy(() => import("./pages/AdminRegister"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const QuotationForm = lazy(
  () => import("./components/dashboards/QuotationForm"),
);
const SellerInvoiceCreationPage = lazy(
  () => import("./components/sellerinvoice/SellerInvoiceCreationPage"),
);
const LogisticsInvoiceCreationPage = lazy(
  () => import("./components/logisticsinvoice/LogisticsInvoiceCreationPage"),
);
const FranchiseInvoiceCreationPage = lazy(
  () => import("./components/franchiseinvoice/FranchiseInvoiceCreationPage"),
);
const BusinessInvoiceCreationPage = lazy(
  () => import("./components/businessinvoice/BusinessInvoiceCreationPage"),
);
const ServiceInvoiceCreationPage = lazy(
  () => import("./components/serviceinvoice/ServiceInvoiceCreationPage"),
);
const Inquiries = lazy(() => import("./components/Inquiries"));
const AdminProductsPage = lazy(() => import("./components/AdminProductsPage"));
const AdminNotificationPage = lazy(
  () => import("./components/AdminNotifications"),
);
const PersonalizationPage = lazy(
  () => import("./components/personalization/Personalization"),
);
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const PrivacyPolicy = lazy(() => import("./components/privacy-policy"));
const CookiePolicy = lazy(() => import("./components/cookie-poilicy"));
const TermsOfService = lazy(() => import("./components/terms-and-services"));
const BuyersTermsAndConditions = lazy(
  () => import("./components/buyer-terms-and-conditions"),
);
const SupportDashboardPage = lazy(() => import("./components/SupportPage"));
const AdminCareers = lazy(() => import("./components/AdminCareers"));
const PolicyManagement = lazy(
  () => import("./components/admin/PolicyManagement"),
);
const VideoManagement = lazy(
  () => import("./components/admin/VideoManagement"),
);
const PolicyBrowser = lazy(() => import("./components/PolicyBrowser"));
const ContactSalesPage = lazy(() => import("./components/ContactSalesPage"));
const Transactions = lazy(() => import("./components/Transactions"));
const AdminCustomQuotes = lazy(
  () => import("./components/dashboards/AdminCustomQuotes"),
);
const RefundPolicyPage = lazy(() => import("./components/refund-policy"));
const ErrorDashboard = lazy(() => import("./components/admin/ErrorDashboard"));
const IPRPolicy = lazy(() => import("./components/IPR-Policy"));
const LogisticsPartnerAgreement = lazy(
  () => import("./components/logistics-partner-agreement"),
);
const MasterTerms = lazy(() => import("./components/master-terms&conditions"));
const PaymentTransactionPolicy = lazy(
  () => import("./components/payment-transaction-policy"),
);
const PrivateWhiteLabelAgreement = lazy(
  () => import("./components/private-white-lable-agreement"),
);
const ProhibitedItemsPolicy = lazy(
  () => import("./components/prohibited-items-policy"),
);
const RawMaterialSellerAgreement = lazy(
  () => import("./components/raw-material-seller-agreement"),
);
const RefundReturnPolicy = lazy(
  () => import("./components/refund-or-return-policy"),
);
const SellerAgreement = lazy(() => import("./components/seller-agreement"));
const ServiceProviderAgreement = lazy(
  () => import("./components/service-provider-agreement"),
);
const ProtectedSellerRoute = lazy(
  () => import("./components/ProtectedSellerRoute"),
);
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const SystemAnalytics = lazy(
  () => import("./components/admin/SystemAnalytics"),
);
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));
const PackagingGuidelinesPage = lazy(() => import("./components/packaging/PackagingGuidelinesPage"));


function App() {
  const [role, setRole] = useState(() => {
    const savedRole = localStorage.getItem("role");
    return savedRole ? savedRole.toUpperCase() : undefined;
  });
  const initialData = UseAll();

  // Handle OAuth success - just set localStorage, Layout.js handles verification popup
  useEffect(() => {
    const handleOAuthSuccess = async () => {
      const params = new URLSearchParams(window.location.search);

      if (params.get("oauth_success") === "true") {
        // Check if this is a new user from URL parameter
        const isNewUserParam = params.get("isNewUser") === "true";

        console.log(
          "✅ OAuth success detected in App.js - isNewUser:",
          isNewUserParam,
        );

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/currentuser`,
            {
              withCredentials: true,
            },
          );

          if (response.data && response.data.user) {
            const user = response.data.user;

            localStorage.setItem("isLoggedIn", "true");
            localStorage.setItem("role", (user.role || "USER").toUpperCase());
            localStorage.setItem("userName", user.username || user.email);
            localStorage.setItem("userProfile", JSON.stringify(user));

            // Set flag for new Google users to show referral popup
            if (isNewUserParam) {
              localStorage.setItem("showGoogleReferralPopup", "true");
              console.log(
                "✅ New Google user - set showGoogleReferralPopup flag",
              );
            }

            window.dispatchEvent(new Event("storage"));

            console.log("✅ OAuth login successful - localStorage updated");

            // Remove oauth_success from URL
            window.history.replaceState({}, document.title, "/");
          }
        } catch (error) {
          console.error("❌ Error fetching user data after OAuth:", error);
        }
      }
    };

    handleOAuthSuccess();
  }, []);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/checkauth`, {
        withCredentials: true,
      })
      .then((response) => {
        const normalizedRole = response.data.role
          ? response.data.role.toUpperCase()
          : response.data.role;
        localStorage.setItem("role", normalizedRole);
        setRole(normalizedRole);
      });
  }, []);

  // Listen for storage events (e.g., dispatched by login/signup pages in the same tab)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedRole = localStorage.getItem("role");
      setRole(savedRole ? savedRole.toUpperCase() : undefined);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Initialize trackers
  useEffect(() => {
    errorTracker.init();
    activityTracker.init();
  }, []);

  return (
    <ToastProvider>
      <TooltipProvider>
        <div className="App">
          <ErrorBoundary>
            <Router>
              <ScrollToTop />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/cookie" element={<CookiePopup />} />
                  <Route path="/admin/register" element={<AdminRegister />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  {/* <Route path="/admin" element={<PrivateRoute></PrivateRoute>} /> */}

                  {/* Order Summary Route - Protected */}
                  <Route
                    path="/order-summary"
                    element={
                      <ProtectedRoute>
                        <OrderSummary />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminHero />} />
                    <Route path="expo-campaign" element={<ExpoLeadsCampaign />} />
                    <Route
                      path="category-management"
                      element={<CategoryManagement initialData={initialData} />}
                    />
                    <Route
                      path="idea-category-management"
                      element={<IdeaCategoryManagement />}
                    />
                    <Route path="banner-update" element={<BannerEdit />} />
                    <Route
                      path="users-dashboard"
                      element={<UsersDashboard />}
                    />
                    <Route
                      path="user-verification"
                      element={<UserVerificationStatus />}
                    />
                    <Route path="inquiries" element={<Inquiries />} />
                    <Route path="products" element={<AdminProductsPage />} />
                    <Route
                      path="notifications"
                      element={<AdminNotificationPage />}
                    />
                    <Route
                      path="system-analytics"
                      element={<SystemAnalytics />}
                    />
                    {/* <Route path="bulk-upload" element={<BulkCategoryUpload />} /> */}
                    <Route
                      path="blogs"
                      element={<BlogsPost title="Admin Blog Management" />}
                    />
                    <Route path="careers" element={<AdminCareers />} />
                    <Route path="policies" element={<PolicyManagement />} />
                    <Route path="videos" element={<VideoManagement />} />
                    <Route path="transactions" element={<Transactions />} />
                    <Route path="logs" element={<ErrorDashboard />} />
                  </Route>
                </Routes>
              </Suspense>

              <Suspense fallback={<LoadingFallback />}>
                <Layout>
                  <Routes>
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/account-status/:status" element={<AccountStatus />} />
                    <Route
                      path="/createsellerinvoice"
                      element={
                        <ProtectedSellerRoute>
                          <SellerInvoiceCreationPage />
                        </ProtectedSellerRoute>
                      }
                    />
                    <Route
                      path="/createlogisticsinvoice"
                      element={
                        <ProtectedRoute>
                          <LogisticsInvoiceCreationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/createfranchiseinvoice"
                      element={
                        <ProtectedRoute>
                          <FranchiseInvoiceCreationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/create-business-invoice"
                      element={
                        <ProtectedRoute>
                          <BusinessInvoiceCreationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/create-service-invoice"
                      element={
                        <ProtectedRoute>
                          <ServiceInvoiceCreationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/orderCompletion/:invoiceID"
                      element={
                        <ProtectedRoute>
                          <PaymentCompletionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/leadsai" element={<LeadScrapingUI />} />
                    <Route path="/joinmerzado" element={<JoinMerzadoPage />} />
                    <Route
                      path="/livestream/watch/:streamid"
                      element={
                        <ProtectedRoute>
                          <WatchLiveStreamPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/rfqresp" element={<RFQResponsesPage />} />
                    <Route
                      path="/rfqresps/:rfqid"
                      element={<RFQResponsePage />}
                    />
                    <Route path="/rfqresps" element={<RFQResponsePage />} />
                    <Route
                      path="/rfq-forms/product"
                      element={<RFQResponsePage />}
                    />
                    <Route
                      path="/rfq-forms/service"
                      element={<RFQResponsePage />}
                    />
                    <Route
                      path="/rfq-forms/logistics"
                      element={<RFQResponsePage />}
                    />
                    <Route
                      path="/rfq-forms/platform-service"
                      element={<RFQResponsePage />}
                    />
                    {/* <Route path="/LiveStreams" element={<LiveStreamsMain />} />
                <Route path="/Startlive" element={<StartLive />} /> */}
                    <Route
                      path="/localsuppliers"
                      element={
                        <ProtectedRoute>
                          <LocalSupplierPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/rfqmainpage"
                      element={
                        <ProtectedVerifiedRoute>
                          <RFQMainPage />
                        </ProtectedVerifiedRoute>
                      }
                    />
                    <Route
                      path="/rfqpostingform"
                      element={<RFQPostingForm />}
                    />
                    <Route
                      path="/rfq/success/:id"
                      element={
                        <ProtectedVerifiedRoute>
                          <RFQSuccessPage />
                        </ProtectedVerifiedRoute>
                      }
                    />
                    <Route
                      path="/rfqdetailspage/:rfqid"
                      element={<RFQDetailsPage />}
                    />
                    <Route path="/idea-hub" element={<IdeaHubPage />} />
                    <Route path="/idea/:id" element={<IdeaDetailsPage />} />
                    {/* <Route path="/mainproductspage" element={<MainProductsPage />} /> */}
                    {/* <Route path="/Manufacture" element={<ManufactureMain />} /> */}
                    {/* <Route path="/" element={<Products />} /> */}
                    {/* <Route path="/Logistics" element={<LogisticsMain />} /> */}
                    <Route
                      path="/ourservices"
                      element={<PlatformServicesPage />}
                    />
                    <Route path="/contactus" element={<ContactUsPage />} />
                    <Route
                      path="/contact-sales"
                      element={<ContactSalesPage />}
                    />
                    <Route path="/packaging-guidelines" element={<PackagingGuidelinesPage />} />
                    <Route path="/onoardingform" element={<OnBoardingForm />} />

                    <Route path="/usecat" element={<UseCategories />} />
                    {/* <Route
                  path="/searcheditems/:input"
                  element={<SearchNavigation />}
                /> */}
                    {/* <Route
                  path="/searcheditemsmanufacturers/:input"
                  element={<SearchNavigate2 />}
                /> */}
                    {/*               <Route
                  path="/singleproduct/:productid"
                  element={<SingleProductCardMain />}
                /> */}

                    {/* {checkauth ? (
                  <Route path="/messagesinboxes" element={<MessageChats />} />
                ) : (
                  <Route
                    path="/messagesinboxes"
                    element={<Navigate to="/login" />}
                  />
                )} */}
                    <Route path="/products" element={<MainProductPage />} />
                    {/* <Route path="/admin/editbanner" element={<BannerEdit />} /> */}
                    <Route
                      path="/productonboardingform"
                      element={
                        <ProtectedSellerRoute>
                          <ProductOnBoardingMain />
                        </ProtectedSellerRoute>
                      }
                    />
                    <Route
                      path="/congratulations"
                      element={<Congratulations />}
                    />
                    {/* <Route
                  path="/manufacturersprofile/:id"
                  element={<ManufacturersSecondMain />}
                /> */}

                    {/* <Route path="/Services" element={<ServicesMain />} /> */}
                    <Route
                      path="/subscriptions"
                      element={
                        <ProtectedRoute>
                          <SubscriptionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/Subscriptions"
                      element={
                        <ProtectedRoute>
                          <SubscriptionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/wishlist" element={<WishlistPage />} />
                    <Route path="/following" element={<FollowingPage />} />
                    <Route
                      path="/accountpage"
                      element={
                        <ProtectedRoute>
                          <AccountPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/personalization"
                      element={<PersonalizationPage />}
                    />

                    {/* <Route path="/Franchises" element={<FranchiseMain />} />
                <Route path="/BuyFranchise" element={<FranchisesSecondPage />} /> */}
                    <Route
                      path="/wallet"
                      element={
                        <ProtectedRoute>
                          <ReferralsAndSubscriptions />
                        </ProtectedRoute>
                      }
                    />
                    {role === "BUSINESS" && (
                      <Route
                        path="/businessonboarding"
                        element={<BusinessOnboarding />}
                      />
                    )}
                    {/* <Route
                  path="/manufacturersprofilepage"
                  element={<ManufacturersProfileForm />}
                /> */}
                    {/* <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminHero />} />
                  <Route
                    path="category-management"
                    element={<CategoryManagement initialData={initialData} />}
                  />
                  <Route path="banner-update" element={<BannerEdit />} />
                  <Route path="users-dashboard" element={<UsersDashboard />} />
                  <Route path="inquiries" element={<Inquiries />} />
                  <Route path="products" element={<AdminProductsPage />} />
                </Route> */}

                    <Route path="/blogspost" element={<BlogsPost />} />

                    <Route path="/searchbyimage" element={<SearchByImage />} />
                    <Route path="/faqPage" element={<FaqPage />} />
                    <Route path="/tips" element={<TipsMainPage />} />
                    <Route
                      path="/tenders"
                      element={
                        <ProtectedRoute>
                          <TendersMainPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tenders/faq"
                      element={
                        <ProtectedRoute>
                          <TenderFAQsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/blog" element={<BlogMainPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route
                      path="/admin/usersdashboard"
                      element={<UsersDashboard />}
                    />
                    <Route element={<AdminLayout />}>
                      <Route
                        path="/admin/custom-quotes"
                        element={<AdminCustomQuotes />}
                      />
                    </Route>
                    <Route
                      path="/userprofile"
                      element={
                        <ProtectedRoute>
                          <UserProfile />
                        </ProtectedRoute>
                      }
                    />
                    {/* <Route path="/contracts" element={<ServicesPage />} />
                <Route path="/contractspage" element={<ContractDetailsPage />} /> */}
                    {/* <Route path="/LiveStreams" element={<LiveStreamsMain />} /> */}
                    {/* <Route path="/Startlive" element={<StartLive />} /> */}
                    <Route
                      path="/livestream"
                      element={
                        <ProtectedRoute>
                          <LiveStreamsMainPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/livestream/start"
                      element={
                        <ProtectedRoute>
                          <StartLivePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/recording/:id"
                      element={<StreamRecording />}
                    />
                    <Route
                      path="/streams"
                      element={
                        <ProtectedRoute>
                          <LiveStreamDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/livestream/:streamid"
                      element={
                        <ProtectedRoute>
                          <LiveStreamPlayer />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/forgotpassword"
                      element={<LoginForgotPasswordPage />}
                    />
                    <Route
                      path="/tradeshows"
                      element={<TradeShowListingPage />}
                    />
                    <Route
                      path="/logisticsonboarding"
                      element={<LogisticsOnboardingForm />}
                    />
                    <Route
                      path="/categorysubcat"
                      element={<CategoryDropdown />}
                    />

                    <Route
                      path="/businessfranchiseonboarding"
                      element={<DynamicPostingForm />}
                    />
                    <Route
                      path="/leadspagemain"
                      element={
                        <ProtectedRoute>
                          <IndustrialLeadsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/serviceonboarding"
                      element={<ServiceOnboardingtemp />}
                    />
                    <Route
                      path="/selleronboarding"
                      element={<SellerOnboarding />}
                    />

                    <Route
                      path="/preonboarding/:type"
                      element={<PreOnboardingPage />}
                    />
                    <Route
                      path="/franchise"
                      element={<FranchiseListingPage />}
                    />
                    <Route
                      path="/franchise/:franchiseId"
                      element={
                        <ProtectedRoute>
                          <FranchiseDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/business" element={<BusinessListingPage />} />
                    <Route
                      path="/business/:id"
                      element={
                        <ProtectedRoute>
                          <BusinessDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/logistics"
                      element={<LogisticsListingPage />}
                    />
                    <Route
                      path="/logistics/:id"
                      element={
                        <ProtectedRoute>
                          <LogisticsDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/services" element={<ServicesListingPage />} />
                    <Route
                      path="/services/:id"
                      element={
                        <ProtectedRoute>
                          <ServiceDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applications/escrow-support"
                      element={<EscrowPreLanding />}
                    />
                    <Route
                      path="/applications/marine-insurance"
                      element={<MarineInsurancePreLanding />}
                    />
                    <Route
                      path="/applications/msme-inspection"
                      element={<MSMEInspectionPreLanding />}
                    />
                    <Route
                      path="/applications/msme-loan"
                      element={<MSMELoanPreLanding />}
                    />
                    <Route
                      path="/products/:productid"
                      element={<SingleProductPage />}
                    />
                    <Route
                      path="/chatcomp"
                      element={<EnhancedChatComponent />}
                    />
                    <Route
                      path="/products/category/:subcategory"
                      element={<SubcategoryProductPage />}
                    />
                    <Route
                      path="/products/type/:productType"
                      element={<ProductTypePage />}
                    />
                    <Route path="/sellers" element={<SellerMainPage />} />
                    <Route
                      path="/sellers/:sellerId"
                      element={
                        <ProtectedRoute>
                          <SellerProfilePageWrapper />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/" element={<B2BHomepage />} />

                    {/* <Route
                  path="/search/products"
                  element={<ProductSearchResults />}
                /> */}
                    {/* new product search routes to support products + bfss - by saurabh */}
                    <Route
                      path="/search/products"
                      element={<ProductSearchResults type="products" />}
                    />
                    <Route
                      path="/search/bfss"
                      element={<ProductSearchResults type="bfss" />}
                    />

                    <Route
                      path="/search/all"
                      element={<CategorySearchResults />}
                    />
                    <Route
                      path="/search/categories"
                      element={<CategorySearchResults />}
                    />

                    <Route path="/careers" element={<Careers />} />
                    <Route path="/get-to-know" element={<GetToKnowPage />} />
                    <Route
                      path="/merzado-services"
                      element={<MerzadoServicesPage />}
                    />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/payments" element={<PaymentsPage />} />
                    {/* ✅ New Footer Legal Pages */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route
                      path="/terms-and-services"
                      element={<TermsOfService />}
                    />
                    <Route path="/cookies" element={<CookiePolicy />} />
                    <Route path="/agreement" element={<SellerAgreement />} />
                    <Route path="/policies" element={<PolicyBrowser />} />
                    <Route path="/policies/:slug" element={<PolicyBrowser />} />
                    <Route
                      path="/buyer-terms-and-conditions"
                      element={<BuyersTermsAndConditions />}
                    />
                    <Route
                      path="/refund-policy"
                      element={<RefundPolicyPage />}
                    />
                    <Route path="/ipr-policy" element={<IPRPolicy />} />
                    <Route
                      path="/logistics-partner-agreement"
                      element={<LogisticsPartnerAgreement />}
                    />
                    <Route
                      path="/master-terms-and-conditions"
                      element={<MasterTerms />}
                    />
                    <Route
                      path="/payment-transaction-policy"
                      element={<PaymentTransactionPolicy />}
                    />
                    <Route
                      path="/private-white-label-agreement"
                      element={<PrivateWhiteLabelAgreement />}
                    />
                    <Route
                      path="/prohibited-items-policy"
                      element={<ProhibitedItemsPolicy />}
                    />
                    <Route
                      path="/raw-material-seller-agreement"
                      element={<RawMaterialSellerAgreement />}
                    />
                    <Route
                      path="/refund-or-return-policy"
                      element={<RefundReturnPolicy />}
                    />
                    <Route
                      path="/seller-agreement"
                      element={<SellerAgreement />}
                    />
                    <Route
                      path="/service-provider-agreement"
                      element={<ServiceProviderAgreement />}
                    />
                    {role === "FRANCHISE" && (
                      <Route
                        path="/dashboard/franchise"
                        element={<FranchiseDashboard />}
                      />
                    )}
                    {(role === "BUSINESS" || role === "RESELLER") && (
                      <Route
                        path="/dashboard/business-services"
                        element={<BusinessServicesDashboard />}
                      />
                    )}
                    {role === "SELLER" && (
                      <Route
                        path="/sellerdashboard"
                        element={
                          <ProtectedSellerRoute>
                            <SellerDashboard />
                          </ProtectedSellerRoute>
                        }
                      />
                    )}
                    {role === "USER" && (
                      <Route
                        path="/userdashboard"
                        element={
                          <ProtectedRoute>
                            <UserDashboard />
                          </ProtectedRoute>
                        }
                      />
                    )}
                    <Route
                      path="/support-dashboard"
                      element={
                        <ProtectedRoute>
                          <SupportDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    {role === "SERVICE" && (
                      <Route
                        path="/dashboard/services"
                        element={<ServiceDashboard />}
                      />
                    )}
                    {role === "LOGISTICS" && (
                      <Route
                        path="/logisticsDashboard"
                        element={<LogisticsDashboardMain />}
                      />
                    )}
                    <Route
                      path="/quotationform"
                      element={
                        <ProtectedRoute>
                          <QuotationForm />
                        </ProtectedRoute>
                      }
                    />

                    {/* <Route path="/cookie" element={<CookiePopup />} />
                <Route path="/admin/register" element={<AdminRegister />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<PrivateRoute></PrivateRoute>} /> */}
                    {/* <Route path="/testdashboard" element={<ComprehensiveDashboard />} /> */}
                    {/* Catch-all 404 route */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>

                  {/* <ManufactureSecondReview /> */}
                  {/* <MessageTemplate/> */}
                  {/* <UsersDashboardData/> */}
                  {/* <CategoryManagement/> */}
                  {/* <NewArrivals/> */}
                  {/* <CustomHTMLMain/> */}
                  {/* <Graphs/> */}
                  {/* <OrdersReceivedPage/> */}
                  {/* <OrdersPage/> */}
                  {/* <MerzadoBusiness/> */}
                  {/* <LeadsPageMain/> */}
                  {/* <SubscriptionPlansMain/> */}
                  {/* <OnBoardingForm /> */}
                  {/* <LogisticsMain /> */}
                  {/* <ProductOnBoardingMain/> */}
                  {/* <ManufacturersSecondMain /> */}
                  {/* <FollowersPage/> */}
                </Layout>
              </Suspense>
            </Router>
          </ErrorBoundary>
        </div>
        {/* <GardenSuppliesMain/> */}
        {/* <MainProductsPage/> */}
        {/* <ManufactureMain/> */}
      </TooltipProvider>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ToastProvider>
  );
}

export default App;
